import { Component } from "@theme/component";
import {
  ThemeEvents,
  CartUpdateEvent,
  ToastEvent
} from "@theme/events";
import { normalizeSectionId, sectionRenderer } from "@theme/section-renderer";
const DEBOUNCE_MS = 250;
class CartFormComponent extends Component {
  #pending = /* @__PURE__ */ new Map();
  #inFlight = 0;
  #section = null;
  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest("[id^='shopify-section-']");
    this.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#onQuantityUpdate
    );
    this.addEventListener("click", this.#onClick);
    this.addEventListener("submit", this.#onSubmit);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#onQuantityUpdate
    );
    this.removeEventListener("click", this.#onClick);
    this.removeEventListener("submit", this.#onSubmit);
    for (const id of this.#pending.values()) clearTimeout(id);
    this.#pending.clear();
  }
  #onQuantityUpdate = (event) => {
    const { quantity, cartLine } = event.detail;
    if (!cartLine) return;
    const existing = this.#pending.get(cartLine);
    if (existing) clearTimeout(existing);
    const timeoutId = window.setTimeout(() => {
      this.#pending.delete(cartLine);
      void this.#change(cartLine, quantity);
    }, DEBOUNCE_MS);
    this.#pending.set(cartLine, timeoutId);
  };
  #onClick = (event) => {
    if (event.defaultPrevented) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const removeButton = target.closest(
      'button[type="submit"][name^="updates["][value="0"]'
    );
    if (!removeButton) return;
    event.preventDefault();
    const form = removeButton.closest("form");
    if (!form) return;
    const buttons = Array.from(
      form.querySelectorAll(
        'button[type="submit"][name^="updates["][value="0"]'
      )
    );
    const line = buttons.indexOf(removeButton) + 1;
    if (line <= 0) return;
    void this.#change(line, 0);
  };
  #onSubmit = (event) => {
    if (event.submitter instanceof HTMLButtonElement && event.submitter.name === "checkout") {
      return;
    }
    event.preventDefault();
    const active = this.ownerDocument.activeElement;
    if (active instanceof HTMLInputElement) active.blur();
  };
  async #change(line, quantity) {
    const body = JSON.stringify({ line, quantity });
    const url = this.#changeUrl();
    this.#inFlight += 1;
    this.#markLineUpdating(line, true);
    this.#markSubtotalUpdating(true);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body
      });
      if (!response.ok) {
        document.dispatchEvent(
          new ToastEvent({
            message: Theme.translations.cart_update_failed,
            variant: "error"
          })
        );
        this.#markLineUpdating(line, false);
        return;
      }
      await this.#rerenderSection();
    } catch {
      document.dispatchEvent(
        new ToastEvent({
          message: Theme.translations.cart_update_failed,
          variant: "error"
        })
      );
      this.#markLineUpdating(line, false);
    } finally {
      this.#inFlight = Math.max(0, this.#inFlight - 1);
      if (this.#inFlight === 0) this.#markSubtotalUpdating(false);
    }
  }
  #markLineUpdating(line, updating) {
    const li = this.querySelector(`li[data-cart-line="${line}"]`);
    if (!li) return;
    if (updating) {
      li.dataset.updating = "true";
      li.setAttribute("aria-busy", "true");
    } else {
      delete li.dataset.updating;
      li.removeAttribute("aria-busy");
    }
  }
  #markSubtotalUpdating(updating) {
    const subtotal = this.querySelector("[data-cart-subtotal]");
    if (!subtotal) return;
    if (updating) {
      subtotal.dataset.updating = "true";
      subtotal.setAttribute("aria-busy", "true");
    } else {
      delete subtotal.dataset.updating;
      subtotal.removeAttribute("aria-busy");
    }
  }
  async #rerenderSection() {
    const section = this.#section;
    if (!section) return;
    await sectionRenderer.renderSection(normalizeSectionId(section.id), {
      cache: false
    });
    document.dispatchEvent(
      new CartUpdateEvent({}, this.id || "cart-form-component", {
        source: "cart-form-component"
      })
    );
  }
  #changeUrl() {
    const base = Theme?.routes?.cart_change_url ?? "/cart/change";
    return base.endsWith(".js") ? base : `${base}.js`;
  }
}
if (!customElements.get("cart-form-component")) {
  customElements.define("cart-form-component", CartFormComponent);
}
export {
  CartFormComponent
};
